/**
 * Remote player representation
 * Handles interpolation between server snapshots
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { PlayerSnapshot } from '@worldify/shared';

/** Player skin color palette - consistent color per player ID */
const PLAYER_COLORS = [
  0xff6b6b, // Red
  0x4ecdc4, // Teal
  0xffe66d, // Yellow
  0x95e1d3, // Mint
  0xf38181, // Coral
  0xaa96da, // Purple
  0x45b7d1, // Blue
  0xf9ca24, // Gold
];

/** Get a consistent color (as 0xRRGGBB) for a player based on their ID */
export function getPlayerColor(playerId: number): number {
  return PLAYER_COLORS[playerId % PLAYER_COLORS.length];
}

/** Convert a 0xRRGGBB number to a CSS hex string like '#ff6b6b' */
export function playerColorToHex(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}

interface PartPlacement { x?: number; y?: number; z?: number; rx?: number; ry?: number; rz?: number; sz?: number }

/** Parts placed and coloured, merged into one geometry. */
class MergedParts {
  private readonly parts: THREE.BufferGeometry[] = [];
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly e = new THREE.Euler();
  private readonly c = new THREE.Color();

  add(source: THREE.BufferGeometry, color: number, at: PartPlacement): void {
    const g = source.clone();
    this.e.set(at.rx ?? 0, at.ry ?? 0, at.rz ?? 0);
    this.m.compose(
      new THREE.Vector3(at.x ?? 0, at.y ?? 0, at.z ?? 0),
      this.q.setFromEuler(this.e),
      new THREE.Vector3(1, 1, at.sz ?? 1),
    );
    g.applyMatrix4(this.m);
    const n = g.getAttribute('position').count;
    const colors = new Float32Array(n * 3);
    this.c.setHex(color);
    for (let i = 0; i < n; i++) { colors[i * 3] = this.c.r; colors[i * 3 + 1] = this.c.g; colors[i * 3 + 2] = this.c.b; }
    g.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    this.parts.push(g);
  }

  build(): THREE.BufferGeometry {
    const merged = mergeGeometries(this.parts, false);
    for (const g of this.parts) g.dispose();
    if (!merged) throw new Error('PlayerRemote: part geometries could not be merged');
    return merged;
  }
}

export class PlayerRemote {
  public readonly playerId: number;
  public readonly mesh: THREE.Group;

  private targetPosition = new THREE.Vector3();
  private targetYaw = 0;
  private currentYaw = 0;

  /** Current interpolated yaw for map overlay */
  get yaw(): number {
    return this.currentYaw;
  }

  constructor(playerId: number) {
    this.playerId = playerId;

    // Server sends eye position (1.6m from ground), so the parts sit below the group origin.
    this.mesh = new THREE.Group();
    const playerColor = getPlayerColor(playerId);
    const skin = 0xffdbac;

    // TWO DRAW CALLS PER PLAYER, not twelve: every part is baked into one of two merged geometries
    // with its colour on the vertices, since nothing here animates independently (the group turns
    // and moves as a whole). Lit parts and flat face marks keep separate materials.
    const lit = new MergedParts();
    lit.add(new THREE.CapsuleGeometry(0.35, 0.8, 8, 16), playerColor, { y: -0.65 });          // body
    lit.add(new THREE.SphereGeometry(0.28, 16, 16), skin, { y: 0.08 });                        // head
    lit.add(new THREE.CylinderGeometry(0.15, 0.2, 0.12, 16), playerColor, { y: 0.32, z: -0.02, rx: -0.15 }); // hat
    lit.add(new THREE.CylinderGeometry(0.22, 0.22, 0.03, 16), playerColor, { y: 0.26, z: -0.08, rx: -0.15 }); // brim
    const body = new THREE.Mesh(lit.build(), new THREE.MeshStandardMaterial({
      vertexColors: true, roughness: 0.6, metalness: 0.05,
    }));
    body.castShadow = true;
    body.receiveShadow = true;
    this.mesh.add(body);

    const face = new MergedParts();
    const eyeWhite = new THREE.SphereGeometry(0.07, 8, 8);
    face.add(eyeWhite, 0xffffff, { x: -0.09, y: 0.12, z: -0.22, sz: 0.5 });
    face.add(eyeWhite, 0xffffff, { x: 0.09, y: 0.12, z: -0.22, sz: 0.5 });
    const pupil = new THREE.SphereGeometry(0.035, 8, 8);
    face.add(pupil, 0x222222, { x: -0.09, y: 0.12, z: -0.26 });
    face.add(pupil, 0x222222, { x: 0.09, y: 0.12, z: -0.26 });
    face.add(new THREE.TorusGeometry(0.08, 0.015, 8, 16, Math.PI), 0x333333, { z: -0.24, rx: Math.PI, rz: Math.PI }); // smile
    // Blush: the old 50% pink over the skin, baked as the mix.
    const blush = new THREE.CircleGeometry(0.04, 16);
    face.add(blush, 0xffc2ab, { x: -0.18, y: 0.02, z: -0.21, ry: 0.4 });
    face.add(blush, 0xffc2ab, { x: 0.18, y: 0.02, z: -0.21, ry: -0.4 });
    this.mesh.add(new THREE.Mesh(face.build(), new THREE.MeshBasicMaterial({ vertexColors: true })));
    eyeWhite.dispose(); pupil.dispose(); blush.dispose();
  }

  applySnapshot(snapshot: PlayerSnapshot): void {
    this.targetPosition.set(snapshot.x, snapshot.y, snapshot.z);
    this.targetYaw = snapshot.yaw;
  }

  update(deltaMs: number): void {
    // Interpolate position smoothly
    const t = Math.min(1, deltaMs / 100);
    this.mesh.position.lerp(this.targetPosition, t);

    // Interpolate yaw (handle wrap-around)
    let yawDiff = this.targetYaw - this.currentYaw;
    // Normalize to [-PI, PI]
    while (yawDiff > Math.PI) yawDiff -= Math.PI * 2;
    while (yawDiff < -Math.PI) yawDiff += Math.PI * 2;
    this.currentYaw += yawDiff * t;
    this.mesh.rotation.y = this.currentYaw;
  }

  dispose(): void {
    // Dispose all child geometries and materials
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
  }
}
